<!-- Page header: the title on the left, the controls that scope the whole page on the right, one
     row. The company and the location are named by the dropdowns that select them, so no scope
     line repeats them under the title. -->
<div class="page-header">
  <app-title-bar
    title="Orders"
    description="Orders across every company and location."
    [showButton]="false"
  >
  </app-title-bar>

  <!-- Scope toolbar: everything below it, cards and table alike, is governed by these controls. -->
  <div class="scope-controls">
    <div class="scope-toolbar">
      <!-- Company Dropdown -->
      <app-simple-dropdown
        class="scope-dropdown"
        [options]="companyOptions"
        [selectedValue]="selectedCompanyId"
        [placeholder]="'Select Company'"
        [title]="'Select Company'"
        [isOpen]="isCompanyDropdownOpen"
        (selectionChange)="selectCompany($event)"
        (toggle)="toggleCompanyDropdown()"
        (close)="closeCompanyDropdown()"
      >
      </app-simple-dropdown>

      <!-- Location Dropdown -->
      <app-simple-dropdown
        class="scope-dropdown"
        *ngIf="selectedCompanyId"
        [options]="locationOptions"
        [selectedValue]="selectedLocationId"
        [placeholder]="'Select Location'"
        [title]="'Select Location'"
        [isOpen]="isLocationDropdownOpen"
        (selectionChange)="selectLocation($event)"
        (toggle)="toggleLocationDropdown()"
        (close)="closeLocationDropdown()"
      >
      </app-simple-dropdown>

      <!-- Date scope: the period the band announces, so it is visible rather than inside the modal. -->
      <app-simple-dropdown
        class="scope-date-dropdown"
        [options]="dateScopeOptions"
        [selectedValue]="dateFilterBy"
        [placeholder]="'Date Range'"
        [title]="'Date Range'"
        [isOpen]="isDateScopeDropdownOpen"
        (selectionChange)="selectDateScope($event)"
        (toggle)="toggleDateScopeDropdown()"
        (close)="closeDateScopeDropdown()"
      >
      </app-simple-dropdown>

      <app-action-button
        buttonText="Filter"
        iconSrc="assets/images/package/adjustments.svg"
        [showCount]="hasActiveFilters"
        [count]="getActiveFilterCount()"
        (buttonClick)="onFilter()"
      >
      </app-action-button>
    </div>

    <!-- Custom range dates always sit on the row under the scope dropdowns. -->
    <div class="scope-custom-range" *ngIf="dateFilterBy === 'custom'">
      <div class="scope-date-field">
        <input
          class="scope-date-input"
          matInput
          [matDatepicker]="dateScopeStartPicker"
          [(ngModel)]="dateStartDate"
          [max]="dateEndDate || null"
          (ngModelChange)="onCustomRangeDateChange()"
          placeholder="Start Date"
          aria-label="Start Date"
        />
        <mat-datepicker-toggle
          class="scope-date-toggle"
          [for]="dateScopeStartPicker"
        ></mat-datepicker-toggle>
        <mat-datepicker #dateScopeStartPicker></mat-datepicker>
      </div>
      <div class="scope-date-field">
        <input
          class="scope-date-input"
          matInput
          [matDatepicker]="dateScopeEndPicker"
          [(ngModel)]="dateEndDate"
          [min]="dateStartDate || null"
          (ngModelChange)="onCustomRangeDateChange()"
          placeholder="End Date"
          aria-label="End Date"
        />
        <mat-datepicker-toggle
          class="scope-date-toggle"
          [for]="dateScopeEndPicker"
        ></mat-datepicker-toggle>
        <mat-datepicker #dateScopeEndPicker></mat-datepicker>
      </div>
    </div>
  </div>
</div>

<!-- Period band: names the one scope the cards below follow, and carries the lifetime totals that
     must never look like cards themselves. -->
<div class="period-band">
  <span class="period-band-label">{{ bandPeriodLabel }}</span>
  <span class="period-band-lifetime" *ngIf="showLifetimeTotals" [attr.aria-label]="lifetimeSummary">
    <span class="lifetime-caption" aria-hidden="true">All time</span>
    <span class="lifetime-figure" *ngFor="let figure of lifetimeFigures" aria-hidden="true">
      <span class="lifetime-value">{{ figure.value }}</span>
      <span class="lifetime-label">{{ figure.label }}</span>
    </span>
  </span>
</div>

<!-- Stats Cards -->
<div class="stats-container">
  <app-stats-card
    *ngFor="let stat of orderStats; let i = index; trackBy: trackByIndex"
    [title]="stat.title"
    [value]="stat.value"
    [valueFontSize]="'30px'"
    [color]="stat.color"
    [icon]="stat.icon"
    [iconClass]="stat.iconClass"
    [iconStyle]="stat.iconStyle"
    [style.animation-delay]="i * 0.1 + 's'"
  >
  </app-stats-card>
</div>

<!-- Table toolbar: search narrows only the rows below it — not the cards, and not the CSV extract,
     which stays company-wide — so it sits with the table instead of above what it does not govern. -->
<div class="table-toolbar" *ngIf="selectedCompanyId">
  <app-searchbar
    [placeholder]="searchPlaceholder"
    [(searchTerm)]="searchTerm"
    (searchChange)="onSearchChange($event)"
    iconSize="small"
  >
  </app-searchbar>

  <div class="table-toolbar-actions">
    <!-- CSV Download Dropdown -->
    <div class="csv-download-dropdown-container">
      <button
        class="btn btn-outlined btn-medium"
        (click)="toggleCsvDownloadDropdown(); $event.stopPropagation()"
      >
        <span>{{ getCsvDownloadLabel() }}</span>
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          [class.rotated]="isCsvDownloadDropdownOpen"
        >
          <path
            d="M5.28 7.38L10 12.1L14.72 7.38"
            stroke="#15172E"
            stroke-width="1"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>

      <div
        class="csv-download-dropdown-menu"
        *ngIf="isCsvDownloadDropdownOpen"
        (click)="$event.stopPropagation()"
      >
        <div class="csv-download-options">
          <div
            class="radio-option"
            *ngFor="let option of csvDownloadOptions; trackBy: trackByValue"
            (click)="selectCsvDownloadOption(option.value)"
          >
            <div class="radio-button" [class.selected]="selectedCsvDownloadOption === option.value">
              <div class="radio-inner" *ngIf="selectedCsvDownloadOption === option.value"></div>
            </div>
            <span
              class="radio-label"
              [class.selected]="selectedCsvDownloadOption === option.value"
              >{{ option.label }}</span
            >
          </div>
        </div>

        <!-- Date inputs for Custom Range - shown inside dropdown -->
        <div class="csv-date-inputs" *ngIf="selectedCsvDownloadOption === 'custom'">
          <div class="date-input-container">
            <mat-form-field
              appearance="outline"
              class="date-picker-field"
              subscriptSizing="dynamic"
            >
              <mat-label>Start Date</mat-label>
              <input
                matInput
                [matDatepicker]="csvStartPicker"
                [(ngModel)]="csvStartDate"
                [max]="csvEndDate || null"
              />
              <mat-datepicker-toggle matIconSuffix [for]="csvStartPicker"></mat-datepicker-toggle>
              <mat-datepicker
                #csvStartPicker
                panelClass="csv-calendar-panel"
                (opened)="onCalendarShow($event)"
                (closed)="onCalendarHide($event)"
              ></mat-datepicker>
            </mat-form-field>
          </div>
          <div class="date-input-container">
            <mat-form-field
              appearance="outline"
              class="date-picker-field"
              subscriptSizing="dynamic"
            >
              <mat-label>End Date</mat-label>
              <input
                matInput
                [matDatepicker]="csvEndPicker"
                [(ngModel)]="csvEndDate"
                [min]="csvStartDate || null"
              />
              <mat-datepicker-toggle matIconSuffix [for]="csvEndPicker"></mat-datepicker-toggle>
              <mat-datepicker
                #csvEndPicker
                panelClass="csv-calendar-panel"
                (opened)="onCalendarShow($event)"
                (closed)="onCalendarHide($event)"
              ></mat-datepicker>
            </mat-form-field>
          </div>
        </div>

        <!-- Apply/Download button -->
        <div class="csv-download-actions" *ngIf="selectedCsvDownloadOption">
          <button
            class="csv-apply-button"
            (click)="onCsvDownloadApply()"
            [disabled]="selectedCsvDownloadOption === 'custom' && (!csvStartDate || !csvEndDate)"
          >
            Download CSV
          </button>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Orders Table (Only show when there are orders to display) -->
<div *ngIf="!loading && orders.length > 0" class="table-container">
  <div class="table-scroll-wrapper" #tableScrollWrapper>
    <table class="orders-table">
      <thead>
        <tr>
          <th (click)="sortByOrderId()" style="cursor: pointer">
            Order ID
            <img [src]="getSortIcon('orderId')" alt="Sort" class="sort-icon" />
          </th>
          <th>Status</th>
          <th (click)="sortByDateTime()" style="cursor: pointer" [title]="dateColumnZoneTitle">
            Date &amp; Time<span class="column-zone" *ngIf="dateColumnZoneLabel">
              ({{ dateColumnZoneLabel }})</span
            >
            <img [src]="getSortIcon('dateTime')" alt="Sort" class="sort-icon" />
          </th>
          <th>Customer Name</th>
          <th>Wash Type</th>
          <th>Order Amount</th>
          <th>Payment Mode</th>
          <th>Phone Number</th>
          <th>List Price</th>
          <th>Voucher Code</th>
          <th>Discount</th>
          <th>Email</th>

          <th>Source</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let order of orders; trackBy: trackByIndex" class="order-row">
          <td class="order-id">
            <button class="order-link" (click)="openOrderDetails(order)">
              {{ order.orderId || '-' }}
            </button>
          </td>
          <td class="status-cell">
            <span class="status-badge" [ngClass]="getStatusClass(order.status)">
              {{ order.status?.label || '-' }}
            </span>
          </td>
          <td class="date-time">{{ order.dateTime || '-' }}</td>
          <td class="customer-name">{{ order.customerName || '-' }}</td>
          <td>
            <span
              class="wash-type-badge"
              [ngClass]="getWashTypeClass(order.washType, order.washTypeValue)"
            >
              {{ order.washType || '-' }}
            </span>
          </td>
          <td class="order-amount">{{ order.price || '-' }}</td>
          <td class="payment-mode">{{ formatPaymentMethod(order.payment_method) || '-' }}</td>
          <td class="phone">{{ order.phone | usaPhoneDisplay }}</td>
          <td class="list-price">{{ order.listPrice || '-' }}</td>
          <td class="voucher-code">{{ order.voucherCode || '-' }}</td>
          <td class="discount">{{ order.discount || '-' }}</td>
          <td class="email">{{ order.email || '-' }}</td>

          <td class="source">{{ getCapitalizedSource(order.source) || '-' }}</td>
          <td class="actions">
            <button
              class="action-button"
              *ngIf="canShowActions(order)"
              (click)="toggleDropdown($event, order.id)"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8 8.5C8.27614 8.5 8.5 8.27614 8.5 8C8.5 7.72386 8.27614 7.5 8 7.5C7.72386 7.5 7.5 7.72386 7.5 8C7.5 8.27614 7.72386 8.5 8 8.5Z"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M8 4.5C8.27614 4.5 8.5 4.27614 8.5 4C8.5 3.72386 8.27614 3.5 8 3.5C7.72386 3.5 7.5 3.72386 7.5 4C7.5 4.27614 7.72386 4.5 8 4.5Z"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M8 12.5C8.27614 12.5 8.5 12.2761 8.5 12C8.5 11.7239 8.27614 11.5 8 11.5C7.72386 11.5 7.5 11.7239 7.5 12C7.5 12.2761 7.72386 12.5 8 12.5Z"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</div>

<!-- Loading State - Shimmer Effect -->
<div *ngIf="loading" class="loading-container">
  <app-loading-table-shimmer
    [columns]="shimmerColumns"
    [rows]="10"
    [showHeader]="false"
    className="scrollable"
  >
  </app-loading-table-shimmer>
</div>

<!-- Empty State - No Orders in the selected period. Named rather than blamed on the company: with
     the date scope defaulting to Today the usual cause is the window, and the way out is offered
     instead of left for the reader to find in the toolbar. -->
<div
  *ngIf="!loading && orders.length === 0 && !hasSearchOrFilters && selectedCompanyId"
  class="empty-state-container"
>
  <app-empty-result
    image="/assets/images/empty_screen.png"
    title="No orders to show"
    [description]="emptyStateDescription"
    [actionText]="canWidenToAllTime ? 'View all time' : ''"
    [showAction]="canWidenToAllTime"
    (actionClicked)="viewAllTime()"
  >
  </app-empty-result>
</div>

<!-- Empty State - No Company Selected -->
<div *ngIf="!loading && !selectedCompanyId" class="empty-state-container">
  <app-empty-result
    image="/assets/images/empty_screen.png"
    title="Select a company"
    description="Please select a company to view orders."
    [actionText]="''"
    [showAction]="false"
  >
  </app-empty-result>
</div>

<!-- Empty State - No Search Results (when search/filters are active but no results) -->
<div
  *ngIf="!loading && orders.length === 0 && hasSearchOrFilters && selectedCompanyId"
  class="empty-state-container"
>
  <app-empty-result
    image="/assets/images/empty_screen.png"
    title="No results found!"
    description="Please try again or modify your search or filters."
    [actionText]="''"
    [showAction]="false"
  >
  </app-empty-result>
</div>

<!-- Pagination -->
<app-pagination-bar
  *ngIf="!loading && orders.length > 0"
  [paginationData]="paginationData"
  [itemsPerPageOptions]="[10, 25, 50]"
  (pageChange)="onPageChange($event)"
  (itemsPerPageChange)="onItemsPerPageChange($event)"
>
</app-pagination-bar>

<!-- Filter Modal -->
<app-filter-modal
  [isVisible]="showFilterModal"
  header="Filters"
  primaryButtonText="Apply Filters"
  secondaryButtonText="Clear Filters"
  [categories]="filterCategories"
  (close)="onCloseFilterModal()"
  (apply)="onFilterModalApply($event)"
  (optionChange)="onFilterOptionChange($event)"
>
</app-filter-modal>

<!-- Order Details Modal -->
<app-super-admin-order-details-modal
  [order]="selectedOrder"
  [isOpen]="showOrderDetailsModal"
  [companyId]="selectedCompanyId"
  (closeModal)="closeOrderDetails()"
  (orderCancelled)="onOrderCancelled($event)"
  (bookAgain)="onBookAgainFromOrderDetails()"
>
</app-super-admin-order-details-modal>

<!-- Cancel Order Modal -->
<app-cancel-order-modal
  [order]="orderToCancel"
  [isOpen]="showCancelOrderModal"
  (close)="closeCancelOrderModal()"
  (confirm)="onCancelOrderConfirm($event)"
>
</app-cancel-order-modal>

<!-- Cancellation Success Modal -->
<app-order-cancellation-success-modal
  [isOpen]="showCancellationSuccessModal"
  [refundAmount]="cancellationRefundAmount"
  [isScheduledOrder]="cancellationWasScheduled"
  (close)="onCancellationSuccessClose()"
  (bookAgain)="onCancellationBookAgain()"
>
</app-order-cancellation-success-modal>

<!-- Global Dropdown Menu -->
<div
  class="actions-dropdown-menu"
  *ngIf="openDropdownId && selectedOrderForDropdown"
  [id]="'dropdown-' + openDropdownId"
>
  <!-- View Action -->
  <div class="dropdown-item" (click)="viewOrder(selectedOrderForDropdown!)">
    <div class="dropdown-content">
      <span class="dropdown-text">View</span>
      <svg
        class="dropdown-icon"
        width="14"
        height="14"
        viewBox="0 0 14 14"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M2.91667 1.75H11.0833C11.5 1.75 11.8333 2.08333 11.8333 2.5V11.5C11.8333 11.9167 11.5 12.25 11.0833 12.25H2.91667C2.5 12.25 2.16667 11.9167 2.16667 11.5V2.5C2.16667 2.08333 2.5 1.75 2.91667 1.75Z"
          stroke="#555A63"
          stroke-width="1"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M5.25 4.08333H8.75"
          stroke="#555A63"
          stroke-width="1"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M5.25 6.41667H8.75"
          stroke="#555A63"
          stroke-width="1"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M5.25 8.75H7"
          stroke="#555A63"
          stroke-width="1"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </div>
  </div>
  <!-- Cancel Action -->
  <!-- <div class="dropdown-item" 
       [class.disabled]="!canCancelOrder(selectedOrderForDropdown!)" 
       (click)="canCancelOrder(selectedOrderForDropdown!) ? cancelOrder(selectedOrderForDropdown!) : null">
    <div class="dropdown-content">
      <span class="dropdown-text">Cancel</span>
      <svg class="dropdown-icon" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1.75 1.75L12.25 12.25" stroke="#DA1E28" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M12.25 1.75L1.75 12.25" stroke="#DA1E28" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </div>
  </div> -->
</div>
